import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CART_QUERY_KEY } from "@/features/cart/constants/cart.constants";
import { cartService } from "@/features/cart/services/cart.service";
import type {
  AddToCartPayload,
  UpdateCartItemPayload,
  RemoveCartItemPayload,
  CartResponse,
  SubsidyChoices,
  ChequeChoices,
} from "@/features/cart/types/cart.type";
import type { AxiosError } from "axios";

const extractErrors = (data: unknown): string[] => {
  if (!data || typeof data !== "object") return [];

  const errors = (data as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors
      .map((msg) => (typeof msg === "string" ? msg : String(msg)))
      .filter(Boolean);
  }

  const message = (data as { message?: unknown }).message;
  if (typeof message === "string" && message.trim()) {
    return [message];
  }

  return [];
};

type CartMutationOptions = { showAddToCartToasts?: boolean };

export const useCartMutations = (options?: CartMutationOptions) => {
  const showAddToCartToasts = options?.showAddToCartToasts ?? true;
  const queryClient = useQueryClient();

  const addToCart = useMutation({
    mutationFn: (payload: AddToCartPayload) => cartService.addToCart(payload),
    onSuccess: (data: CartResponse) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
      const friendlyErrors = extractErrors(data);
      if (friendlyErrors.length) {
        if (showAddToCartToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showAddToCartToasts) {
        toast.success("Article ou offre ajouté(e) au panier");
      }
    },
    onError: (error: AxiosError) => {
      const friendlyErrors = extractErrors(error.response?.data);
      if (friendlyErrors.length) {
        if (showAddToCartToasts) {
          toast.error(friendlyErrors.join("\n"));
        }
        return;
      }
      if (showAddToCartToasts) {
        toast.error("Impossible d'ajouter cet élément au panier");
      }
    },
  });

  const saveSeatSelection = useMutation({
    mutationFn: cartService.saveSeatSelection,
    onSuccess: (data: CartResponse) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
      toast.success("Places ajoutées au panier");
    },
    onError: (error: AxiosError) => {
      toast.error(extractErrors(error.response?.data).join("\n") || "Impossible d’enregistrer les places. Veuillez réessayer.");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY }),
  });

  const updateCartItem = useMutation({
    mutationFn: (payload: UpdateCartItemPayload) => cartService.updateCartItem(payload),
    onSuccess: (data: CartResponse) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
      toast.success("Panier mis à jour");
    },
    onError: (error: AxiosError) => {
      toast.error(extractErrors(error.response?.data).join("\n") || "Impossible de mettre à jour le panier");
    },
  });

  const removeCartItem = useMutation({
    mutationFn: (payload: RemoveCartItemPayload) => cartService.removeCartItem(payload),
    onSuccess: (data: CartResponse) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
      toast.success("Article ou offre retiré(e) du panier");
    },
    onError: (error: AxiosError) => {
      toast.error(extractErrors(error.response?.data).join("\n") || "Impossible de retirer cet élément du panier");
    },
  });

  const updateSubsidyChoices = useMutation({
    mutationFn: (payload: SubsidyChoices) => cartService.updateSubsidyChoices(payload),
    onMutate: async (newChoices: SubsidyChoices) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });
      const previousCart = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY);

      if (previousCart) {
        queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, {
          ...previousCart,
          subsidy_choices: newChoices,
        });
      }

      return { previousCart };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previousCart);
      }
      toast.error("Impossible de mettre à jour les subventions");
    },
    onSuccess: (data: CartResponse) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
    },
  });

  const updateChequeChoices = useMutation({
    mutationFn: (payload: ChequeChoices) => cartService.updateChequeChoices(payload),
    onMutate: async (newChoices: ChequeChoices) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY });
      const previousCart = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY);

      if (previousCart) {
        queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, {
          ...previousCart,
          cheque_choices: newChoices,
        });
      }

      return { previousCart };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previousCart);
      }
      toast.error("Impossible de mettre à jour les chèques");
    },
    onSuccess: (data: CartResponse) => {
      queryClient.setQueryData(CART_QUERY_KEY, data);
    },
  });

  return {
    addToCart,
    saveSeatSelection,
    updateCartItem,
    removeCartItem,
    updateSubsidyChoices,
    updateChequeChoices,
  };
};
